import { type ComponentPropsWithoutRef, type ComponentType, memo, useMemo } from 'react';

import { type BaseCustomField, type CustomFieldValue } from '@air/api/types';

import { BOARD_STATS_BAR_HEIGHT } from '~/_components/BoardStatsBar';
import { AirDndContext } from '~/components/KanbanView/AirDndContext';
import { KanbanColumnsManagers } from '~/components/KanbanView/KanbanColumnsManagers/KanbanColumnsManagers';
import { KanbanLoadingState } from '~/components/KanbanView/KanbanLoadingState';
import { useGetSingleSelectCustomFields } from '~/components/KanbanView/useGetSingleSelectCustomFields';
import { KANBAN_VIEW_CONTAINER } from '~/constants/testIDs';

export type KanbanViewProps = Pick<ComponentPropsWithoutRef<'div'>, 'style'> & {
  NullState: ComponentType;
  tableHeaderTopPos?: number;
  customFields: BaseCustomField[] | undefined;
  baseCustomField: BaseCustomField | undefined;
  customFieldValues: CustomFieldValue[] | undefined;
  canViewCustomFields: boolean;
};

export const KanbanView = memo(
  ({
    NullState,
    tableHeaderTopPos,
    customFields,
    baseCustomField,
    customFieldValues,
    canViewCustomFields,
    style: styleProp,
  }: KanbanViewProps) => {
    const { hasSingleSelectCustomFields } = useGetSingleSelectCustomFields(customFields);
    const loadingState = useMemo(() => <KanbanLoadingState />, []);

    const style = useMemo(
      () => ({
        height: `calc(100vh - ${(tableHeaderTopPos ?? 0) + BOARD_STATS_BAR_HEIGHT}px)`,
        ...styleProp,
      }),
      [styleProp, tableHeaderTopPos],
    );

    if (!canViewCustomFields) {
      return <NullState />;
    }

    return (
      <AirDndContext>
        <div className="size-full ads:bg-raised bg-gray-2 py-4" data-testid={KANBAN_VIEW_CONTAINER} style={style}>
          {customFields ? (
            // biome-ignore lint/style/noNestedTernary: the columns, empty, and loading branches stay one child of the board container
            hasSingleSelectCustomFields ? (
              <KanbanColumnsManagers
                loadingState={loadingState}
                baseCustomField={baseCustomField}
                customFieldValues={customFieldValues}
              />
            ) : (
              <NullState />
            )
          ) : (
            loadingState
          )}
        </div>
      </AirDndContext>
    );
  },
);

KanbanView.displayName = 'KanbanView';
